/**
 * One sub-delegate card on the owner management page (active relationship
 * or pending invitation) — Feature 075 (plan 013, C-T05).
 */

import { useState } from 'react';
import { Pencil, RefreshCw, Trash2, X } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/shared/components/ui/alert-dialog';
import {
  DelegateStatusBadge,
  EditPermissionsDialog,
  PermissionBadgeList,
  type DelegationPermission,
} from '@/shared/components/sub-delegation';
import type {
  ActiveDelegate,
  PendingDelegate,
} from '../../types/subDelegateManagement';

type DelegateCardProps =
  | {
      kind: 'active';
      delegate: ActiveDelegate;
      isMutating: boolean;
      isSavingPermissions: boolean;
      onRevoke: (relationshipId: string) => void;
      onUpdatePermissions: (
        relationshipId: string,
        permissions: DelegationPermission[],
      ) => void;
    }
  | {
      kind: 'pending';
      delegate: PendingDelegate;
      isMutating: boolean;
      onResend: (pendingId: string) => void;
      onCancel: (pendingId: string) => void;
    };

const initialsOf = (first: string, last: string | null): string =>
  `${first.charAt(0)}${(last ?? '').charAt(0)}`.toUpperCase() || '?';

const daysUntil = (iso: string): number =>
  Math.ceil((new Date(iso).getTime() - Date.now()) / (1000 * 60 * 60 * 24));

const actionButtonClass =
  'flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50';

export function DelegateCard(props: DelegateCardProps) {
  if (props.kind === 'active') return <ActiveCard {...props} />;
  return <PendingCard {...props} />;
}

function ActiveCard({
  delegate,
  isMutating,
  isSavingPermissions,
  onRevoke,
  onUpdatePermissions,
}: Extract<DelegateCardProps, { kind: 'active' }>) {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const displayName =
    [delegate.delegateFirstName, delegate.delegateLastName]
      .filter(Boolean)
      .join(' ') || delegate.delegateEmail;

  return (
    <li
      className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
      data-testid={`active-delegate-${delegate.id}`}
    >
      <div className="flex items-start gap-3">
        <span
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#026857]/10 text-sm font-semibold text-[#026857]"
          aria-hidden="true"
        >
          {initialsOf(delegate.delegateFirstName, delegate.delegateLastName)}
        </span>
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate text-sm font-semibold text-gray-900">
              {displayName}
            </span>
            <DelegateStatusBadge status="ACTIVE" />
          </div>
          <div className="truncate text-xs text-gray-500">
            {delegate.delegateEmail} · joined{' '}
            {new Date(delegate.createdAt).toLocaleDateString()}
          </div>
          <PermissionBadgeList permissions={delegate.permissions} />
        </div>
      </div>

      <div className="mt-3 flex justify-end gap-2 border-t border-gray-100 pt-3">
        <button
          type="button"
          onClick={() => setIsEditOpen(true)}
          disabled={isMutating || isSavingPermissions}
          className={`${actionButtonClass} border-gray-200 text-gray-700 hover:border-[#026857]/40 hover:text-[#026857]`}
          aria-label={`Edit permissions for ${displayName}`}
        >
          <Pencil className="h-3.5 w-3.5" />
          Edit permissions
        </button>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button
              type="button"
              disabled={isMutating}
              className={`${actionButtonClass} border-gray-200 text-gray-700 hover:border-red-200 hover:bg-red-50 hover:text-red-600`}
              aria-label={`Revoke ${displayName}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
              Revoke
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Revoke workspace access?</AlertDialogTitle>
              <AlertDialogDescription>
                {displayName} will immediately lose access to your workspace.
                This cannot be undone — you would need to send a new
                invitation.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep access</AlertDialogCancel>
              <AlertDialogAction onClick={() => onRevoke(delegate.id)}>
                Revoke access
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <EditPermissionsDialog
        open={isEditOpen}
        onOpenChange={setIsEditOpen}
        delegateName={displayName}
        initialPermissions={delegate.permissions}
        isSaving={isSavingPermissions}
        onSave={(permissions) => {
          onUpdatePermissions(delegate.id, permissions);
          setIsEditOpen(false);
        }}
      />
    </li>
  );
}

function PendingCard({
  delegate,
  isMutating,
  onResend,
  onCancel,
}: Extract<DelegateCardProps, { kind: 'pending' }>) {
  const displayName =
    [delegate.firstName, delegate.lastName].filter(Boolean).join(' ') ||
    delegate.email;
  const remainingDays = daysUntil(delegate.expiresAt);
  const isExpired = remainingDays <= 0;

  return (
    <li
      className="rounded-xl border border-amber-200/70 bg-amber-50/40 p-4"
      data-testid={`pending-delegate-${delegate.id}`}
    >
      <div className="flex items-start gap-3">
        <span
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 text-sm font-semibold text-amber-700"
          aria-hidden="true"
        >
          {initialsOf(delegate.firstName, delegate.lastName)}
        </span>
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate text-sm font-semibold text-gray-900">
              {displayName}
            </span>
            <DelegateStatusBadge status={isExpired ? 'EXPIRED' : 'PENDING'} />
            <span className="text-[11px] text-amber-700">
              {isExpired
                ? `Expired ${new Date(delegate.expiresAt).toLocaleDateString()}`
                : `Expires in ${remainingDays} day${remainingDays === 1 ? '' : 's'}`}
            </span>
          </div>
          <div className="truncate text-xs text-gray-500">{delegate.email}</div>
          <PermissionBadgeList permissions={delegate.permissions} />
        </div>
      </div>

      <div className="mt-3 flex justify-end gap-2 border-t border-amber-100 pt-3">
        <button
          type="button"
          onClick={() => onResend(delegate.id)}
          disabled={isMutating}
          className={`${actionButtonClass} border-gray-200 bg-white text-gray-700 hover:border-[#026857]/40 hover:text-[#026857]`}
          aria-label={`Resend invitation to ${delegate.email}`}
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Resend
        </button>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button
              type="button"
              disabled={isMutating}
              className={`${actionButtonClass} border-gray-200 bg-white text-gray-700 hover:border-red-200 hover:bg-red-50 hover:text-red-600`}
              aria-label={`Cancel invitation to ${delegate.email}`}
            >
              <X className="h-3.5 w-3.5" />
              Cancel
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Cancel this invitation?</AlertDialogTitle>
              <AlertDialogDescription>
                The invitation link sent to {delegate.email} will stop working.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep invitation</AlertDialogCancel>
              <AlertDialogAction onClick={() => onCancel(delegate.id)}>
                Cancel invitation
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </li>
  );
}
